/**
 * 변수 속성(ID/Type/Default/Format) 목록 입력 컨트롤.
 *
 * 겉모습은 SnCodeBox와 동일한 한 줄 입력칸 + 돋보기 버튼이다. 값이 목록이라
 * 직접 타이핑은 막고(readOnly), 돋보기를 누르면 목록 편집 팝업이 뜬다.
 * 입력칸에는 등록된 변수 ID를 콤마로 이어 보여준다.
 *
 * 값은 VarProps[]를 JSON 문자열로 주고받는다 — jsonb 컬럼(js_param)에 그대로 실린다.
 */
import React, { useState } from "react";
import { IconButton, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { ClearIcon } from "@mui/x-date-pickers";
import { showPopupModal } from "@widgets/CommonModal";
import { StyleSettings } from "../../styles/StyleSettings";
import VarPropsListPopup from "@widgets/commonpopup/VarPropsListPopup";
import { type VarProps } from "@widgets/commonpopup/VarPropsPopup";

interface SnVarPropsBoxProps {
  /** 현재 목록 */
  value: VarProps[];
  /** 목록이 바뀌면 호출 */
  onChange: (next: VarProps[]) => void;
  isReadOnly?: boolean;
  isRequired?: boolean;
  /** 행 미선택 등으로 입력 대상이 없을 때 (다른 Sn* 컨트롤과 동일하게 회색 처리) */
  isEditable?: boolean;
  inputRef?: any;
  /** 다른 Sn* 컨트롤과 동일한 포커스 이동 규약 */
  onFocus?: (target: string) => void;
}

const SnVarPropsBox = React.memo(
  ({
    value,
    onChange,
    isReadOnly = false,
    isRequired,
    isEditable = true,
    inputRef,
    onFocus,
  }: SnVarPropsBoxProps) => {
    const [isFocused, setFocused] = useState(false);
    const [isMouseOver, setMouseOver] = useState(false);

    const rows = Array.isArray(value) ? value : [];
    // 한 줄이라 전부는 못 보여준다. ID만 이어 붙이고 나머지는 팝업에서 확인.
    const displayValue = rows
      .map((r) => r?.id)
      .filter(Boolean)
      .join(", ");

    const borderWidth = isRequired ? "2px" : "1px";
    const backgroundColor = isReadOnly || !isEditable ? "#F1F1F1" : "white";

    const openPopup = async () => {
      const result = await showPopupModal({
        title: "변수 속성",
        children: (
          <VarPropsListPopup defaultValue={rows} readOnly={isReadOnly} />
        ),
        // 그리드 도움창이라 높이를 고정한다 (auto면 그리드가 접힌다)
        width: "720px",
        height: "500px",
      });
      // 취소/X는 undefined 또는 "close"
      if (!result || result === "close" || result === "ok") return;
      const next = (result as any).varProps;
      if (Array.isArray(next)) {
        onChange(next);
        onFocus?.("next");
      }
    };

    return (
      <div className="relative w-full" style={{ padding: 0 }}>
        <TextField
          inputRef={inputRef}
          value={displayValue}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onMouseOver={() => setMouseOver(true)}
          onMouseOut={() => setMouseOver(false)}
          placeholder={isReadOnly ? "" : "돋보기를 눌러 변수를 등록하세요"}
          fullWidth
          disabled={isReadOnly}
          slotProps={{
            input: {
              autoComplete: "off",
              // 목록형 값이라 직접 타이핑은 막는다 (팝업으로만 편집)
              readOnly: true,
              endAdornment: (
                <div style={{ display: "flex", alignItems: "center" }}>
                  {(isFocused || isMouseOver) && !isReadOnly && displayValue && (
                    <IconButton
                      edge="end"
                      onClick={() => onChange([])}
                      onMouseDown={(event) => event.preventDefault()}
                      size="small"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      openPopup();
                    }}
                    edge="end"
                    tabIndex={-1}
                    size="small"
                    disabled={isReadOnly}
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </div>
              ),
            },
          }}
          sx={{
            backgroundColor,
            borderWidth,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderWidth: borderWidth,
              },
              "&:hover fieldset": {
                borderWidth: borderWidth,
              },
              "&.Mui-focused fieldset": {
                borderWidth: borderWidth,
              },
            },
            "& .MuiInputBase-root": {
              height: StyleSettings.control_height,
              paddingRight: 1,
              "&.Mui-disabled": {
                backgroundColor: "#f5f5f5",
              },
            },
            "& .MuiInputBase-input": {
              fontSize: StyleSettings.font,
            },
          }}
        />
      </div>
    );
  }
);

export default SnVarPropsBox;
